import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { payWithRazorpay, fetchRazorpayConfig } from "../../lib/razorpay";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Minus, Trash2, ShoppingBag, Search, ChevronRight, RotateCcw } from "lucide-react";
import { toast, Toaster } from "sonner";
import OrderReviewDialog from "../../components/OrderReviewDialog";

export default function DistributorShop() {
    const { profile } = useOutletContext() || {};
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [cart, setCart] = useState([]); // [{product_id,size_id,quantity,name,size_label,price}]
    const [address, setAddress] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
    const [submitting, setSubmitting] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("razorpay");
    const [gateway, setGateway] = useState({ enabled: false, key_id: "", mode: null });

    const load = async () => {
        try { const { data } = await api.get("/distributors/products", { params: { q: q || undefined } }); setProducts(data); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed to load products"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    useEffect(() => {
        fetchRazorpayConfig().then((cfg) => {
            setGateway(cfg);
            if (!cfg.enabled) setPaymentMethod("cod");
        });
    }, []);

    useEffect(() => {
        if (profile) setAddress((a) => ({
            ...a,
            name: a.name || profile.business_name,
            phone: a.phone || profile.phone,
            line1: a.line1 || profile.address_line1,
            line2: a.line2 || profile.address_line2 || "",
            city: a.city || profile.city,
            state: a.state || profile.state,
            pincode: a.pincode || profile.pincode,
        }));
    }, [profile]);

    const addToCart = (p, v) => {
        setCart(cur => {
            const idx = cur.findIndex(x => x.product_id === p.id && x.size_id === v.size_id);
            if (idx >= 0) {
                const c = [...cur]; c[idx] = { ...c[idx], quantity: c[idx].quantity + 1 }; return c;
            }
            return [...cur, { product_id: p.id, size_id: v.size_id, name: p.name, size_label: v.size_label, price: v.distributor_price || v.price, quantity: 1 }];
        });
    };
    const updateQty = (i, delta) => setCart(cur => cur.map((x, idx) => idx === i ? { ...x, quantity: Math.max(0, x.quantity + delta) } : x).filter(x => x.quantity > 0));
    const setQty = (i, n) => setCart(cur => cur.map((x, idx) => idx === i ? { ...x, quantity: Math.max(1, parseInt(n, 10) || 1) } : x));
    const remove = (i) => setCart(cur => cur.filter((_, idx) => idx !== i));
    const subtotal = cart.reduce((s, x) => s + x.price * x.quantity, 0);

    const place = async () => {
        if (cart.length === 0) return toast.error("Cart is empty");
        // Tell the user EXACTLY which field is missing — generic "Fill shipping
        // address" caused confusion in the past.
        const REQUIRED = [
            ["name", "Business / customer name"],
            ["phone", "Phone"],
            ["line1", "Address line 1"],
            ["city", "City"],
            ["state", "State"],
            ["pincode", "Pincode"],
        ];
        for (const [k, label] of REQUIRED) {
            if (!String(address[k] || "").trim()) {
                return toast.error(`${label} is required in the shipping address`);
            }
        }
        setSubmitting(true);
        try {
            const payload = {
                items: cart.map(c => ({ product_id: c.product_id, size_id: c.size_id, quantity: c.quantity })),
                address,
                payment_method: paymentMethod,
            };
            if (paymentMethod === "razorpay") {
                await payWithRazorpay({ purpose: "distributor", order: payload });
                toast.success("Payment successful — order confirmed");
            } else {
                await api.post("/distributors/orders", payload);
                toast.success("Order placed");
            }
            setCart([]);
            setReviewOpen(false);
        } catch (e) {
            if (e.cancelled) toast.error(e.message);
            else toast.error(e.response?.data?.detail || e.message || "Could not place order");
        }
        finally { setSubmitting(false); }
    };

    return (
        <div data-testid="dist-shop" className="space-y-4">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Wholesale catalog</div>
            <h1 className="font-serif text-4xl text-stone-900">Place an order</h1>

            <div className="bg-white border border-stone-200 p-3 flex gap-2">
                <div className="flex-1 flex items-center px-3"><Search className="w-4 h-4 text-stone-400 mr-2" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="border-0 rounded-none focus-visible:ring-0 px-0" data-testid="dist-shop-search" />
                </div>
                <Button onClick={load} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Search</Button>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-3">
                    {products.length === 0 ? <div className="bg-white border border-stone-200 p-12 text-center text-sm text-stone-500">No products available yet.</div> : (
                        products.map(p => (
                            <div key={p.id} className="bg-white border border-stone-200 p-4" data-testid={`dist-prod-${p.slug}`}>
                                <div className="flex items-start gap-3 mb-2">
                                    <div className="w-14 h-14 bg-stone-100 shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                                    <div className="flex-1">
                                        <div className="font-serif text-lg">{p.name}</div>
                                        <div className="text-xs text-stone-500 line-clamp-1">{p.indications}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                                    {p.variants?.length ? p.variants.map(v => (
                                        <button key={v.size_id} onClick={() => addToCart(p, v)} className="text-left border border-stone-200 p-2 hover:border-sky-700 transition-colors" data-testid={`dist-add-${p.slug}-${v.size_label}`}>
                                            <div className="font-mono text-xs">{v.size_label}</div>
                                            <div className="text-sky-700 text-sm font-medium">{formatINR(v.distributor_price || v.price)}</div>
                                            {v.distributor_price && v.distributor_price !== v.price && (
                                                <div className="text-[10px] text-stone-400 line-through">{formatINR(v.price)}</div>
                                            )}
                                        </button>
                                    )) : <span className="text-xs text-stone-500">No sizes</span>}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <aside className="lg:col-span-1">
                    <div className="bg-white border border-stone-200 sticky top-4">
                        <div className="p-4 border-b border-stone-100 bg-sky-50/40">
                            <div className="label-eyebrow text-sky-700">Your order</div>
                            <div className="font-serif text-2xl mt-1 text-slate-900">{cart.length === 0 ? "Empty" : `${cart.length} item${cart.length === 1 ? "" : "s"}`}</div>
                            {cart.length > 0 && <div className="text-xs text-stone-500 mt-1">{cart.reduce((n, x) => n + x.quantity, 0)} units · {formatINR(subtotal)}</div>}
                        </div>
                        <div className="max-h-[480px] overflow-auto">
                            {cart.length === 0 ? (
                                <div className="p-8 text-center text-sm text-stone-500">
                                    Tap a size from the catalog to add it to your order.
                                </div>
                            ) : cart.map((c, i) => (
                                <div key={`${c.product_id}-${c.size_id}`} className="p-3 border-b border-stone-100 last:border-b-0" data-testid={`dist-cart-row-${i}`}>
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-slate-900 line-clamp-2">{c.name}</div>
                                            <div className="text-[11px] text-stone-500 mt-0.5">{c.size_label} · {formatINR(c.price)} <span className="text-stone-400">each</span></div>
                                        </div>
                                        <button onClick={() => remove(i)} className="text-stone-400 hover:text-red-700 p-1 -m-1" title="Remove" data-testid={`dist-cart-remove-${i}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => updateQty(i, -1)}
                                                className="w-9 h-11 border border-stone-300 hover:border-sky-700 hover:bg-sky-50 grid place-items-center transition-colors"
                                                data-testid={`dist-qty-minus-${i}`}
                                                aria-label="Decrease quantity"
                                            >
                                                <Minus className="w-4 h-4 text-stone-600" />
                                            </button>
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                min={1}
                                                value={c.quantity}
                                                onChange={(e) => setQty(i, e.target.value)}
                                                onFocus={(e) => e.target.select()}
                                                onBlur={(e) => { if (!e.target.value || parseInt(e.target.value, 10) < 1) setQty(i, 1); }}
                                                className="w-20 h-11 text-center text-lg font-semibold font-mono border-2 border-amber-400 focus:border-sky-700 focus:outline-none bg-amber-50 focus:bg-white"
                                                data-testid={`dist-qty-${i}`}
                                                aria-label={`Quantity for ${c.name}`}
                                            />
                                            <button
                                                onClick={() => updateQty(i, 1)}
                                                className="w-9 h-11 border border-stone-300 hover:border-sky-700 hover:bg-sky-50 grid place-items-center transition-colors"
                                                data-testid={`dist-qty-plus-${i}`}
                                                aria-label="Increase quantity"
                                            >
                                                <Plus className="w-4 h-4 text-stone-600" />
                                            </button>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-widest text-stone-400">Line total</div>
                                            <div className="font-mono font-semibold text-sky-700 text-base" data-testid={`dist-line-total-${i}`}>{formatINR(c.price * c.quantity)}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-stone-100 space-y-2 bg-stone-50/50">
                            <div className="flex justify-between text-sm"><span className="text-stone-600">Subtotal</span><span className="font-semibold font-mono">{formatINR(subtotal)}</span></div>
                        </div>
                        <div className="px-4 py-3 border-t border-stone-100 space-y-2" data-testid="dist-payment-method">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Payment</div>
                            <button
                                type="button"
                                onClick={() => gateway.enabled && setPaymentMethod("razorpay")}
                                disabled={!gateway.enabled}
                                className={`w-full text-left border px-3 py-2 text-xs transition-colors ${paymentMethod === "razorpay" ? "border-sky-700 bg-sky-50" : "border-stone-200"} ${gateway.enabled ? "" : "opacity-50 cursor-not-allowed"}`}
                                data-testid="dist-pay-razorpay"
                            >
                                <span className="font-semibold text-stone-800">Pay now · Razorpay</span>
                                <span className="block text-[10px] text-stone-500">{gateway.enabled ? "UPI / RTGS / card — instant confirmation" : "Temporarily unavailable"}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setPaymentMethod("cod")}
                                className={`w-full text-left border px-3 py-2 text-xs transition-colors ${paymentMethod === "cod" ? "border-sky-700 bg-sky-50" : "border-stone-200"}`}
                                data-testid="dist-pay-cod"
                            >
                                <span className="font-semibold text-stone-800">Pay on delivery / credit</span>
                                <span className="block text-[10px] text-stone-500">Settle against your account statement</span>
                            </button>
                        </div>
                        <details className="px-4 py-2 border-t border-stone-100">
                            <summary className="text-xs uppercase tracking-widest text-stone-500 cursor-pointer">Shipping address</summary>
                            <div className="mt-3 space-y-2">
                                {[
                                    ["name", "Name"], ["phone", "Phone"], ["line1", "Address line 1"], ["line2", "Address line 2"],
                                    ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
                                ].map(([k, label]) => (
                                    <div key={k}><Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
                                        <Input value={address[k]} onChange={(e) => setAddress(a => ({ ...a, [k]: e.target.value }))} className="rounded-none border-stone-300 mt-1 h-8" /></div>
                                ))}
                            </div>
                        </details>
                        <Button onClick={() => setReviewOpen(true)} disabled={submitting || cart.length === 0} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full py-5" data-testid="dist-review-open">
                            <ShoppingBag className="w-4 h-4 mr-2" />Review &amp; place order
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </aside>
            </div>

            <OrderReviewDialog
                open={reviewOpen}
                onOpenChange={setReviewOpen}
                cart={cart}
                setQty={setQty}
                updateQty={updateQty}
                removeItem={remove}
                address={address}
                setAddress={setAddress}
                addressFields={[
                    ["name", "Business name *"],
                    ["phone", "Phone *"],
                    ["line1", "Address line 1 *"],
                    ["line2", "Address line 2"],
                    ["city", "City *"],
                    ["state", "State *"],
                    ["pincode", "Pincode *"],
                ]}
                addressNote="Required for shipping"
                addressHeaderSlot={profile ? (
                    <div className="flex items-center justify-between bg-sky-50/60 border border-sky-200 px-3 py-2" data-testid="dist-review-profile-banner">
                        <div className="text-xs text-stone-700">
                            <span className="text-[10px] uppercase tracking-widest text-sky-900 font-semibold mr-2">Profile</span>
                            Shipping to <strong>{profile.business_name}</strong>{profile.city ? ` · ${profile.city}` : ""}
                        </div>
                        <Button
                            onClick={() => setAddress({
                                name: profile.business_name || "",
                                phone: profile.phone || "",
                                line1: profile.address_line1 || "",
                                line2: profile.address_line2 || "",
                                city: profile.city || "",
                                state: profile.state || "",
                                pincode: profile.pincode || "",
                            })}
                            variant="outline"
                            size="sm"
                            className="rounded-none border-stone-300 h-7 px-2 text-xs"
                            data-testid="dist-review-reset-address"
                        >
                            <RotateCcw className="w-3 h-3 mr-1" />Reset to my profile
                        </Button>
                    </div>
                ) : null}
                submitting={submitting}
                onConfirm={place}
                testIdPrefix="dist"
            />
        </div>
    );
}
